import {useCallback} from 'react';
import {t} from '../../../../../../common/getTranslations';
import {requestContext} from '../../../../../../common/requestContext';
import {twMerge} from '../../../../../../common/twMerge';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {ViewerSetting} from '../../../../../../design-system/organisms/ViewerSetting';
import {ViewerSettingFontType} from '../../../../../../design-system/organisms/ViewerSetting/ViewerSettingFontItemType.interface';
import {ViewerSettingReadType} from '../../../../../../design-system/organisms/ViewerSetting/ViewerSettingReadTypeProps.interface';
import {ViewerSettingThemeType} from '../../../../../../design-system/organisms/ViewerSetting/ViewerSettingThemeItemType.interface';
import {
  CONTENT_MARGIN_DEFAULT,
  CONTENT_MARGIN_MAX,
  CONTENT_MARGIN_MIN,
  FONT_FAMILY_DEFAULT,
  FONT_SIZE_DEFAULT,
  FONT_SIZE_MAX,
  FONT_SIZE_MIN,
  LINE_GAP_DEFAULT,
  LINE_GAP_MAX,
  LINE_GAP_MIN,
  LOCAL_STORAGE_VIEWER_SETTING_CONTENT_MARGIN_KEY,
  LOCAL_STORAGE_VIEWER_SETTING_FONT_FAMILY_KEY,
  LOCAL_STORAGE_VIEWER_SETTING_FONT_SIZE_KEY,
  LOCAL_STORAGE_VIEWER_SETTING_IS_TWO_PAGE_LAYOUT,
  LOCAL_STORAGE_VIEWER_SETTING_LINE_GAP_KEY,
  LOCAL_STORAGE_VIEWER_SETTING_READ_TYPE_KEY,
  LOCAL_STORAGE_VIEWER_SETTING_THEME_KEY,
  THEME_DEFAULT,
  ViewerType,
} from '../constant/viewerConstant';
import {
  changeFontFamily,
  changeReadType,
  changeTheme,
  decreaseContentMargin,
  decreaseFontSize,
  decreaseLineGap,
  increaseContentMargin,
  increaseFontSize,
  increaseLineGap,
  resetContentOptions,
  toggleTwoPageLayout,
} from '../state/textViewerSlice';
import {setCurrentViewerGuide} from '../state/viewerInfoSlice';
import {TextViewerReadType} from '../type/TextViewerReadType';
import {convertViewerReadTypeToGuideType} from '../util/convertViewerReadTypeToGuideType';
import {saveViewerSettingToLocalStorage} from '../util/saveViewerSettingToLocalStorage';

export function TextViewerSettingContainer({...props}) {
  const {isMobile} = requestContext.requestInfo.display;
  const dispatch = useTypedDispatch();

  const {
    textViewerTheme,
    textViewerFontFamily,
    textViewerFontSize,
    textViewerLineGap,
    textViewerContentMargin,
    textViewerIsTwoPageLayout,
    textViewerReadType,
  } = useTypedSelector(state => ({
    textViewerTheme: state.textViewer.settings.theme,
    textViewerFontFamily: state.textViewer.settings.fontFamily,
    textViewerFontSize: state.textViewer.settings.fontSize,
    textViewerLineGap: state.textViewer.settings.lineGap,
    textViewerContentMargin: state.textViewer.settings.contentMargin,
    textViewerIsTwoPageLayout: state.textViewer.settings.isTwoPageLayout,
    textViewerReadType: state.textViewer.settings.readType,
  }));

  const onClickTheme = useCallback(
    (clickedTheme: ViewerSettingThemeType) => {
      saveViewerSettingToLocalStorage(
        ViewerType.TextViewer,
        LOCAL_STORAGE_VIEWER_SETTING_THEME_KEY,
        clickedTheme
      );
      dispatch(changeTheme(clickedTheme));
    },
    [dispatch]
  );

  const onClickFontFamily = useCallback(
    (clickedFontType: ViewerSettingFontType) => {
      saveViewerSettingToLocalStorage(
        ViewerType.TextViewer,
        LOCAL_STORAGE_VIEWER_SETTING_FONT_FAMILY_KEY,
        clickedFontType
      );
      dispatch(changeFontFamily(clickedFontType));
    },
    [dispatch]
  );

  const onClickFontSizeMinus = useCallback(() => {
    saveViewerSettingToLocalStorage(
      ViewerType.TextViewer,
      LOCAL_STORAGE_VIEWER_SETTING_FONT_SIZE_KEY,
      (textViewerFontSize - 1).toString()
    );
    dispatch(decreaseFontSize());
  }, [dispatch, textViewerFontSize]);

  const onClickFontSizePlus = useCallback(() => {
    saveViewerSettingToLocalStorage(
      ViewerType.TextViewer,
      LOCAL_STORAGE_VIEWER_SETTING_FONT_SIZE_KEY,
      (textViewerFontSize + 1).toString()
    );
    dispatch(increaseFontSize());
  }, [dispatch, textViewerFontSize]);

  const onClickLineGapMinus = useCallback(() => {
    saveViewerSettingToLocalStorage(
      ViewerType.TextViewer,
      LOCAL_STORAGE_VIEWER_SETTING_LINE_GAP_KEY,
      (textViewerLineGap - 1).toString()
    );
    dispatch(decreaseLineGap());
  }, [dispatch, textViewerLineGap]);

  const onClickLineGapPlus = useCallback(() => {
    saveViewerSettingToLocalStorage(
      ViewerType.TextViewer,
      LOCAL_STORAGE_VIEWER_SETTING_LINE_GAP_KEY,
      (textViewerLineGap + 1).toString()
    );
    dispatch(increaseLineGap());
  }, [dispatch, textViewerLineGap]);

  const onClickContentMarginMinus = useCallback(() => {
    saveViewerSettingToLocalStorage(
      ViewerType.TextViewer,
      LOCAL_STORAGE_VIEWER_SETTING_CONTENT_MARGIN_KEY,
      (textViewerContentMargin - 1).toString()
    );
    dispatch(decreaseContentMargin());
  }, [dispatch, textViewerContentMargin]);

  const onClickContentMarginPlus = useCallback(() => {
    saveViewerSettingToLocalStorage(
      ViewerType.TextViewer,
      LOCAL_STORAGE_VIEWER_SETTING_CONTENT_MARGIN_KEY,
      (textViewerContentMargin + 1).toString()
    );
    dispatch(increaseContentMargin());
  }, [dispatch, textViewerContentMargin]);

  //초기화 버튼 클릭. 로컬스토리지, 리덕스를 기본값으로 초기화
  const onClickResetContentOptions = useCallback(() => {
    saveViewerSettingToLocalStorage(
      ViewerType.TextViewer,
      LOCAL_STORAGE_VIEWER_SETTING_THEME_KEY,
      THEME_DEFAULT
    );
    saveViewerSettingToLocalStorage(
      ViewerType.TextViewer,
      LOCAL_STORAGE_VIEWER_SETTING_FONT_FAMILY_KEY,
      FONT_FAMILY_DEFAULT
    );
    saveViewerSettingToLocalStorage(
      ViewerType.TextViewer,
      LOCAL_STORAGE_VIEWER_SETTING_FONT_SIZE_KEY,
      FONT_SIZE_DEFAULT.toString()
    );
    saveViewerSettingToLocalStorage(
      ViewerType.TextViewer,
      LOCAL_STORAGE_VIEWER_SETTING_LINE_GAP_KEY,
      LINE_GAP_DEFAULT.toString()
    );
    saveViewerSettingToLocalStorage(
      ViewerType.TextViewer,
      LOCAL_STORAGE_VIEWER_SETTING_CONTENT_MARGIN_KEY,
      CONTENT_MARGIN_DEFAULT.toString()
    );

    dispatch(resetContentOptions());
  }, [dispatch]);

  const onClickTwoLayoutToggle = useCallback(() => {
    saveViewerSettingToLocalStorage(
      ViewerType.TextViewer,
      LOCAL_STORAGE_VIEWER_SETTING_IS_TWO_PAGE_LAYOUT,
      (!textViewerIsTwoPageLayout).toString()
    );
    dispatch(toggleTwoPageLayout());
  }, [dispatch, textViewerIsTwoPageLayout]);

  const onClickReadType = useCallback(
    (clickedReadType: ViewerSettingReadType) => {
      const clickedtextViewerReadType =
        clickedReadType === 'Type1'
          ? TextViewerReadType.Page
          : TextViewerReadType.Scroll;

      saveViewerSettingToLocalStorage(
        ViewerType.TextViewer,
        LOCAL_STORAGE_VIEWER_SETTING_READ_TYPE_KEY,
        clickedtextViewerReadType
      );
      dispatch(changeReadType(clickedtextViewerReadType));
      dispatch(
        setCurrentViewerGuide(
          convertViewerReadTypeToGuideType({
            viewerType: ViewerType.TextViewer,
            viewerReadType: clickedtextViewerReadType,
          })
        )
      );
    },
    [dispatch]
  );

  return (
    <ViewerSetting {...props}>
      {/* 콘텐츠 보기 설정 영역 */}
      <div className={twMerge('px-24pxr', isMobile && 'pt-8pxr')}>
        <ViewerSetting.ReadType
          isMobile={isMobile}
          readType1={t('viewer_setting_mode_page_button')}
          readType2={t('viewer_setting_mode_scroll_button')}
          isReadType1Disabled={false}
          currentReadType={
            textViewerReadType === TextViewerReadType.Page ? 'Type1' : 'Type2'
          }
          onClickReadType={onClickReadType}
        />
        <ViewerSetting.Divider isMobile={isMobile} />
        <ViewerSetting.Theme
          isMobile={isMobile}
          currentTheme={textViewerTheme}
          onClickTheme={onClickTheme}
        />
        <ViewerSetting.FontFamily
          isMobile={isMobile}
          currentFontType={textViewerFontFamily}
          onClickFontFamily={onClickFontFamily}
        />
        <ViewerSetting.FontSize
          isMobile={isMobile}
          currentFontSize={textViewerFontSize}
          onClickFontSizeMinus={onClickFontSizeMinus}
          onClickFontSizePlus={onClickFontSizePlus}
          isFontSizeMinusDisabled={textViewerFontSize <= FONT_SIZE_MIN}
          isFontSizePlusDisabled={textViewerFontSize >= FONT_SIZE_MAX}
        />
        <ViewerSetting.LineGap
          isMobile={isMobile}
          currentLineGap={textViewerLineGap}
          onClickLineGapMinus={onClickLineGapMinus}
          onClickLineGapPlus={onClickLineGapPlus}
          isLineGapMinusDisabled={textViewerLineGap <= LINE_GAP_MIN}
          isLineGapPlusDisabled={textViewerLineGap >= LINE_GAP_MAX}
        />
        <ViewerSetting.ContentMargin
          isMobile={isMobile}
          currentContentMargin={textViewerContentMargin}
          onClickContentMarginMinus={onClickContentMarginMinus}
          onClickContentMarginPlus={onClickContentMarginPlus}
          isContentMarginMinusDisabled={
            textViewerContentMargin <= CONTENT_MARGIN_MIN
          }
          isContentMarginPlusDisabled={
            textViewerContentMargin >= CONTENT_MARGIN_MAX
          }
        />
        <ViewerSetting.ResetContentOptions
          isMobile={isMobile}
          onClickResetContentOptions={onClickResetContentOptions}
        />
      </div>

      {!isMobile && (
        <div>
          <ViewerSetting.Divider isMobile={isMobile} />
          <ViewerSetting.TwoPageLayout
            isMobile={isMobile}
            isDisabled={textViewerReadType === TextViewerReadType.Scroll}
            isTwoPageLayout={textViewerIsTwoPageLayout}
            onClickTwoLayoutToggle={onClickTwoLayoutToggle}
            className={twMerge('px-24pxr', !isMobile && 'h-40pxr')}
          />
        </div>
      )}
    </ViewerSetting>
  );
}
